const SESSION_ID_KEY = "chat_session_id";
const CHAT_HISTORY_KEY = "chat_history";
const LAST_ACTIVITY_KEY = "chat_last_activity";
export const GLOBAL_CHAT_ID = "__global__";

/** Record now as the chat's last activity (decides whether to welcome a visitor back). */
export function touchChatActivity(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  } catch {
    // storage unavailable: the visitor is simply treated as returning
  }
}

/** Epoch ms of the chat's last activity, or null when unknown. */
export function getLastChatActivity(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const value = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function getSessionKey(pathname?: string): string {
  if (!pathname || pathname === GLOBAL_CHAT_ID) return SESSION_ID_KEY;
  return `${SESSION_ID_KEY}_${pathname}`;
}

function getHistoryKey(pathname?: string): string {
  if (!pathname || pathname === GLOBAL_CHAT_ID) return CHAT_HISTORY_KEY;
  return `${CHAT_HISTORY_KEY}_${pathname}`;
}

export type StoredChatMessage = {
  role: "user" | "assistant";
  content: string;
  isInitial?: boolean;
  structured?: any;
  propertyData?: any[];
};

function generateUUID(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getOrCreateSessionId(pathname?: string): string {
  if (typeof window === "undefined") {
    return generateUUID();
  }

  try {
    const sessionKey = getSessionKey(pathname);
    let sessionId = localStorage.getItem(sessionKey);

    if (!sessionId) {
      sessionId = generateUUID();
      localStorage.setItem(sessionKey, sessionId);
    }

    return sessionId;
  } catch (error) {
    console.error("Error accessing localStorage for session ID:", error);
    return generateUUID();
  }
}

export function saveChatHistory(messages: StoredChatMessage[], pathname?: string): void {
  if (typeof window === "undefined") return;

  try {
    const historyKey = getHistoryKey(pathname);
    localStorage.setItem(
      historyKey,
      JSON.stringify(
        messages.map((message) => ({
          role: message.role,
          content: message.content,
          ...(message.isInitial ? { isInitial: true } : {}),
          ...(message.structured ? { structured: message.structured } : {}),
          ...(message.propertyData ? { propertyData: message.propertyData } : {}),
        })),
      ),
    );
  } catch (error) {
    console.error("Error saving chat history to localStorage:", error);
  }
}

/** True when persisted history includes a real assistant API reply (not welcome bubbles only). */
export function hasAssistantApiResponseInHistory(history: StoredChatMessage[]): boolean {
  return history.some(
    (msg) =>
      msg.role === "assistant" &&
      !msg.isInitial &&
      ((Array.isArray(msg.structured) && msg.structured.length > 0) ||
        (Array.isArray(msg.propertyData) && msg.propertyData.length > 0)),
  );
}

export function loadChatHistory(pathname?: string): StoredChatMessage[] {
  if (typeof window === "undefined") return [];

  try {
    const historyKey = getHistoryKey(pathname);
    const stored = localStorage.getItem(historyKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error("Error loading chat history from localStorage:", error);
  }

  return [];
}

/** If the global chat is empty, adopt the current page's older per-page session once. */
export function migratePageSessionToGlobal(pathname?: string): void {
  if (typeof window === "undefined" || !pathname) return;

  try {
    if (localStorage.getItem(SESSION_ID_KEY)) return;

    const globalHistory = loadChatHistory();
    if (globalHistory.length > 0) return;

    const pageHistory = loadChatHistory(pathname);
    if (pageHistory.length === 0) return;

    saveChatHistory(pageHistory);

    const pageSessionId = localStorage.getItem(getSessionKey(pathname));
    if (pageSessionId) {
      localStorage.setItem(SESSION_ID_KEY, pageSessionId);
    }
  } catch (error) {
    console.error("Error migrating page chat session to global:", error);
  }
}

export function clearChatHistory(pathname?: string): void {
  if (typeof window === "undefined") return;

  try {
    const historyKey = getHistoryKey(pathname);
    localStorage.removeItem(historyKey);
  } catch (error) {
    console.error("Error clearing chat history:", error);
  }
}

export function resetChatSession(pathname?: string): string {
  if (typeof window === "undefined") return generateUUID();

  try {
    clearChatHistory(pathname);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    const sessionKey = getSessionKey(pathname);
    const newSessionId = generateUUID();
    localStorage.setItem(sessionKey, newSessionId);
    return newSessionId;
  } catch (error) {
    console.error("Error resetting chat session:", error);
    return generateUUID();
  }
}

export function getAllChatHistories(): Array<{ pathname: string; messageCount: number; lastMessage?: string }> {
  if (typeof window === "undefined") return [];

  try {
    const histories: Array<{ pathname: string; messageCount: number; lastMessage?: string }> = [];

    const pushHistory = (pathname: string, stored: string | null) => {
      if (!stored) return;
      try {
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed) || parsed.length === 0) return;
        const lastMessage = parsed[parsed.length - 1];
        histories.push({
          pathname,
          messageCount: parsed.length,
          lastMessage:
            lastMessage?.structured?.[lastMessage?.structured?.length - 1]?.htmlValue ||
            parsed[parsed.length - 1]?.content?.slice(0, 100),
        });
      } catch (error) {
        console.error(`Error parsing history for ${pathname}:`, error);
      }
    };

    pushHistory(GLOBAL_CHAT_ID, localStorage.getItem(CHAT_HISTORY_KEY));

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CHAT_HISTORY_KEY + "_")) {
        const pathname = key.replace(CHAT_HISTORY_KEY + "_", "");
        pushHistory(pathname, localStorage.getItem(key));
      }
    }

    return histories.sort((a, b) => {
      if (a.pathname === GLOBAL_CHAT_ID) return -1;
      if (b.pathname === GLOBAL_CHAT_ID) return 1;
      return b.messageCount - a.messageCount;
    });
  } catch (error) {
    console.error("Error getting all chat histories:", error);
    return [];
  }
}

export function deleteChatHistory(pathname: string): void {
  if (typeof window === "undefined") return;

  try {
    const historyKey = getHistoryKey(pathname);
    const sessionKey = getSessionKey(pathname);
    localStorage.removeItem(historyKey);
    localStorage.removeItem(sessionKey);
  } catch (error) {
    console.error("Error deleting chat history:", error);
  }
}

